import {
  getSurfaceState,
  type OnboardingBundle,
  type OnboardingSurface,
  type TraderProfile,
} from "../../lib/onboarding-v2";
import type { WelcomeStep } from "./welcome-onboarding-types";

const MAX_AVATAR_OUTPUT_BYTES = 420_000;

export function profileFromBundle(bundle: OnboardingBundle): TraderProfile {
  const existing = bundle.onboarding.profile;
  const avatarUrl = existing?.avatarUrl ?? bundle.profile?.avatar_url ?? null;
  return {
    avatarUrl,
    billingTier: "consul-beta",
    brokerOrPropFirm: existing?.brokerOrPropFirm ?? "",
    deskId: existing?.deskId ?? "priced-in-capital",
    deskName: existing?.deskName ?? "",
    nameTag: existing?.nameTag ?? bundle.profile?.display_name ?? "",
    yearsExperience: existing?.yearsExperience ?? 0,
  };
}

export function emptyProfile(): TraderProfile {
  return {
    avatarUrl: null,
    billingTier: "consul-beta",
    brokerOrPropFirm: "",
    deskId: "priced-in-capital",
    deskName: "",
    nameTag: "",
    yearsExperience: 0,
  };
}

export function nextIncompleteStep(
  bundle: OnboardingBundle,
  surface: OnboardingSurface,
  profile: TraderProfile,
): WelcomeStep {
  const surfaceState = getSurfaceState(bundle.onboarding, surface);
  if (!profile.nameTag.trim()) return "profile";
  if (surfaceState.permissions?.status !== "complete") return "permissions";
  if (!surfaceState.desk?.status) return "desk";
  if (!surfaceState.aiProvider?.status) return "ai";
  return "ai";
}

export function resizeAvatar(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Unable to read image"));
    reader.onload = () => {
      img.onload = () => {
        const size = Math.min(640, Math.max(img.width, img.height));
        const scale = size / Math.max(img.width, img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Unable to process image"));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(compressCanvas(canvas));
      };
      img.onerror = () => reject(new Error("Unsupported image"));
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

function compressCanvas(canvas: HTMLCanvasElement): string {
  let quality = 0.82;
  let dataUrl = canvas.toDataURL("image/jpeg", quality);
  while (dataUrl.length > MAX_AVATAR_OUTPUT_BYTES && quality > 0.42) {
    quality -= 0.08;
    dataUrl = canvas.toDataURL("image/jpeg", quality);
  }
  return dataUrl;
}
